import type {
  DoubaoTtsGenerationView,
  DoubaoTtsProviderConfigView,
  StoryListItem,
} from "@actale/contracts";
import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { api, ApiError, storyVoiceAudioUrl } from "./api.js";
import { formatUpdatedAt } from "./story-utils.js";

const LIGHTHOUSE_STORY_SLUG = "mist-over-lighthouse";
const TTS_2_RESOURCE_ID = "seed-tts-2.0";
const MIA_TENSE_PERFORMANCE_INSTRUCTION = "你是年轻的气象研究员米娅。补给船即将靠岸，但负责接应的林叔已经失联三天。你努力保持专业和冷静，但明显压不住担心。不要播音腔，不要哭腔。开头压低声音、稍慢；说到“电话、无线电”时因为焦急略微加快；“都联系不上”加重并短暂停顿；最后“这不像他”明显放慢、压低，带着不安和判断。句子之间保留自然呼吸。";

const statusLabels: Record<DoubaoTtsGenerationView["status"], string> = {
  SUBMITTING: "提交中，结果待确认",
  SUCCEEDED: "已生成",
  FAILED: "失败",
};

const errorLabels: Record<string, string> = {
  DOUBAO_TTS_PROVIDER_AUTH_FAILED: "API Key 或模型权限无效，请检查语音模型配置。",
  DOUBAO_TTS_PROVIDER_QUOTA_OR_RATE_LIMIT: "供应商额度不足或请求过于频繁，请先检查账号额度。",
  DOUBAO_TTS_PROVIDER_REJECTED: "供应商拒绝了本次请求，请检查文本、音色权限和资源 ID。",
  DOUBAO_TTS_SUBMISSION_OUTCOME_UNKNOWN: "网络中断，供应商是否已生成无法确认。系统不会自动重试，请先检查火山账单或任务记录。",
  DOUBAO_TTS_PROVIDER_EMPTY_AUDIO: "供应商没有返回可用音频。",
  DOUBAO_TTS_PROVIDER_INVALID_RESPONSE: "供应商返回内容无法识别。",
  DOUBAO_TTS_PERFORMANCE_INSTRUCTION_REQUIRED: "TTS 2.0 必须填写表演指令，不能只靠自动演绎。",
  DOUBAO_TTS_MEDIA_INVALID: "供应商返回的文件不是有效 MP3。",
  DOUBAO_TTS_MEDIA_TOO_LARGE: "供应商返回的音频超过 15MB。",
  DOUBAO_TTS_GENERATION_FAILED: "生成或保存音频失败。",
};

function message(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

function replaceGeneration(
  current: DoubaoTtsGenerationView[],
  next: DoubaoTtsGenerationView,
): DoubaoTtsGenerationView[] {
  return [next, ...current.filter((item) => item.id !== next.id)];
}

export function DoubaoTtsGenerationPage({
  onConfigureProvider,
}: {
  onConfigureProvider?: () => void;
}): React.ReactNode {
  const [config, setConfig] = useState<DoubaoTtsProviderConfigView | null>(null);
  const [stories, setStories] = useState<StoryListItem[]>([]);
  const [generations, setGenerations] = useState<DoubaoTtsGenerationView[]>([]);
  const [storyId, setStoryId] = useState("");
  const [label, setLabel] = useState("");
  const [text, setText] = useState("");
  const [performanceInstruction, setPerformanceInstruction] = useState("");
  const [costConfirmed, setCostConfirmed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [nextConfig, storyResult, nextGenerations] = await Promise.all([
        api.doubaoTtsProviderConfig(),
        api.storyLines({ pageSize: 100 }),
        api.doubaoTtsGenerations(),
      ]);
      setConfig(nextConfig);
      setStories(storyResult.data);
      setGenerations(nextGenerations);
      setStoryId((current) => current
        || storyResult.data.find((story) => story.slug === LIGHTHOUSE_STORY_SLUG)?.id
        || storyResult.data[0]?.id
        || "");
    } catch (caught) {
      setError(message(caught, "无法读取语音生成工作台"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const textBytes = useMemo(() => new TextEncoder().encode(text.trim()).length, [text]);
  const instructionBytes = useMemo(
    () => new TextEncoder().encode(performanceInstruction.trim()).length,
    [performanceInstruction],
  );
  const selectedStory = stories.find((story) => story.id === storyId) ?? null;
  const usesTts2 = config?.resourceId === TTS_2_RESOURCE_ID;
  const instructionValid = !usesTts2 || Boolean(
    performanceInstruction.trim()
    && performanceInstruction.trim().length <= 500
    && instructionBytes <= 1_500,
  );
  const ready = Boolean(
    config?.configured
    && storyId
    && label.trim()
    && text.trim()
    && textBytes <= 1_024
    && instructionValid
    && costConfirmed
    && !submitting,
  );

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!ready || !selectedStory) return;
    const instruction = performanceInstruction.trim();
    const performance = config!.resourceId === TTS_2_RESOURCE_ID
      ? instruction
      : `${config!.emotion} · 强度 ${config!.emotionScale}`;
    if (!window.confirm(`确认付费生成“${label.trim()}”？\n\n故事线：《${selectedStory.title}》\n模型：${config!.resourceId}\n音色：${config!.speakerId}\n表演要求：${performance}\n\n本次会把台词和表演要求发送给火山豆包语音合成并产生费用。系统不会自动重试，也不会修改故事草稿或发布 StoryVersion。`)) return;
    setSubmitting(true);
    setError("");
    setNotice("");
    try {
      const next = await api.createDoubaoTtsGeneration({
        clientRequestId: crypto.randomUUID(),
        storyId,
        label: label.trim(),
        text: text.trim(),
        ...(usesTts2 ? { performanceInstruction: instruction } : {}),
        costConfirmed: true,
      });
      setGenerations((current) => replaceGeneration(current, next));
      setCostConfirmed(false);
      if (next.status === "SUCCEEDED") {
        setNotice("语音已生成并保存到 ACTALE，可以在下方直接播放。故事草稿和 StoryVersion 均未修改。");
      } else {
        setError(errorLabels[next.errorCode ?? ""] ?? "语音生成失败，请查看下方任务记录。");
      }
    } catch (caught) {
      setError(message(caught, "语音生成提交失败"));
      try {
        setGenerations(await api.doubaoTtsGenerations());
      } catch {
        // Preserve the original actionable submission error.
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (loading && !config) {
    return <div className="director-loading"><span /><strong>正在读取语音生成工作台…</strong></div>;
  }
  if (!config) {
    return <div className="state-card"><div className="error-notice" role="alert"><span>!</span>{error || "语音生成工作台不可用"}</div><button className="secondary-button" onClick={() => void load()}>重新加载</button></div>;
  }

  return (
    <div className="tts-generation-page">
      <div className="page-heading provider-config-heading">
        <div>
          <p className="eyebrow">AI VOICE STUDIO</p>
          <h1>语音生成</h1>
          <p>用已配置的豆包音色生成故事对白；先生成和试听，再决定是否交给口型视频模型。</p>
        </div>
        <button className="secondary-button" disabled={loading || submitting} onClick={() => void load()}>{loading ? "正在刷新…" : "刷新工作台"}</button>
      </div>

      {error ? <div className="error-notice" role="alert"><span>!</span>{error}</div> : null}
      {notice ? <div className="director-notice" role="status">✓ {notice}</div> : null}

      <form className="tts-generation-form" onSubmit={(event) => void submit(event)}>
        <header>
          <div>
            <span className={`provider-state-dot ${config.configured ? "ready" : "empty"}`} />
            <div><strong>新建语音任务</strong><small>豆包语音合成 {config.configured ? "已配置" : "未配置"} · MP3 / 24kHz</small></div>
          </div>
          <span className={`provider-config-status ${config.configured ? "ready" : "empty"}`}>不会修改故事</span>
        </header>
        {!config.configured ? <div className="tts-config-required" role="alert"><div><strong>豆包语音尚未配置</strong><span>请先在“AI 模型设置”中保存语音 API 密钥。</span></div><button type="button" className="secondary-button" onClick={onConfigureProvider}>前往 AI 模型设置</button></div> : null}
        <div className="tts-generation-grid">
          <label>
            <span>关联故事线</span>
            <select value={storyId} onChange={(event) => setStoryId(event.target.value)} required>
              <option value="">请选择故事线</option>
              {stories.map((story) => <option key={story.id} value={story.id}>{story.title} · 草稿 R{story.draftRevision}</option>)}
            </select>
          </label>
          <label>
            <span>任务名称</span>
            <input value={label} onChange={(event) => setLabel(event.target.value)} maxLength={120} placeholder="例如：第1段 · 米娅对白" required />
          </label>
          <label className="tts-text-field">
            <span>要生成的台词</span>
            <textarea value={text} onChange={(event) => setText(event.target.value)} rows={5} maxLength={1_024} placeholder="只填写最终确认的对白；页面不会自动发送或自动重试。" required />
            <small className={textBytes > 1_024 ? "invalid" : ""}>{textBytes} / 1,024 字节</small>
          </label>
          {usesTts2 ? <label className="tts-text-field">
            <span className="tts-field-heading">
              <span>表演指令（TTS 2.0 必填）</span>
              {selectedStory?.slug === LIGHTHOUSE_STORY_SLUG ? <button type="button" className="secondary-button" onClick={() => setPerformanceInstruction(MIA_TENSE_PERFORMANCE_INSTRUCTION)}>填入米娅紧张版</button> : null}
            </span>
            <textarea value={performanceInstruction} onChange={(event) => setPerformanceInstruction(event.target.value)} rows={6} maxLength={500} placeholder="说明角色处境、情绪变化、哪里加快或放慢、哪里停顿。指令不会被朗读。" required />
            <small className={instructionBytes > 1_500 ? "invalid" : ""}>{performanceInstruction.trim().length} / 500 字符 · {instructionBytes} / 1,500 字节</small>
          </label> : null}
        </div>
        <div className="tts-model-snapshot">
          <div><span>音色</span><strong>{config.speakerId}</strong></div>
          <div><span>演绎</span><strong>{config.resourceId === TTS_2_RESOURCE_ID ? "台词 + 表演指令" : `${config.emotion} · 强度 ${config.emotionScale}`}</strong></div>
          <div><span>资源</span><strong>{config.resourceId}</strong></div>
        </div>
        <label className="tts-cost-confirm">
          <input type="checkbox" checked={costConfirmed} onChange={(event) => setCostConfirmed(event.target.checked)} />
          <span><strong>我确认本次提交会产生豆包语音合成费用</strong><small>文本会发送给火山；系统不会自动重试，不会自动交给视频模型。</small></span>
        </label>
        <footer>
          <span>生成只会新增一条可试听素材；不会修改草稿，不会发布 StoryVersion。</span>
          <button className="primary-button compact" disabled={!ready}>{submitting ? "正在生成，请勿重复提交…" : "确认并生成语音"}</button>
        </footer>
      </form>

      <section className="tts-generation-history">
        <header><div><strong>最近语音任务</strong><small>每条任务保留实际音色、台词、表演指令和结果</small></div><span>{generations.length} 条</span></header>
        {!generations.length ? <div className="empty-state"><span className="empty-glyph" aria-hidden="true">声</span><h2>还没有正式语音任务</h2><p>配置完成不等于已生成；提交首条任务后才会产生费用和音频。</p></div> : null}
        <div className="tts-generation-list">
          {generations.map((generation) => {
            const audioUrl = generation.managedAudioKey ? storyVoiceAudioUrl(generation.managedAudioKey) : null;
            return <article className={`tts-generation-item ${generation.status.toLowerCase()}`} key={generation.id}>
              <header>
                <span>{statusLabels[generation.status]}</span>
                <div><strong>{generation.label}</strong><small>《{generation.story.title}》 · {formatUpdatedAt(generation.createdAt)}</small></div>
              </header>
              <p>{generation.text}</p>
              {audioUrl ? <div className="tts-audio-result">
                <audio src={audioUrl} controls preload="metadata" />
                <a className="secondary-button" href={audioUrl} target="_blank" rel="noreferrer">打开或下载 MP3</a>
              </div> : null}
              {generation.status === "FAILED" ? <div className="seedance-job-error"><strong>{generation.errorCode ?? "DOUBAO_TTS_GENERATION_FAILED"}</strong><span>{errorLabels[generation.errorCode ?? ""] ?? "请检查配置、文本和供应商状态后再决定是否重新提交。"}</span></div> : null}
              {generation.status === "SUBMITTING" ? <div className="tts-pending-note">这条任务没有完成结果确认。系统不会自动重试，避免重复扣费。</div> : null}
              <details><summary>查看生成参数</summary><dl>
                <div><dt>音色</dt><dd>{generation.speakerId}</dd></div>
                <div><dt>演绎</dt><dd>{generation.resourceId === TTS_2_RESOURCE_ID ? "2.0 语义自动演绎" : `${generation.emotion} · ${generation.emotionScale}`}</dd></div>
                <div><dt>格式</dt><dd>{generation.audioFormat} · {generation.sampleRate}Hz</dd></div>
                <div><dt>文件</dt><dd>{generation.managedAudioBytes ? `${(generation.managedAudioBytes / 1024).toFixed(1)}KB` : "—"}</dd></div>
                {generation.resourceId === TTS_2_RESOURCE_ID ? <div className="tts-parameter-wide"><dt>表演指令</dt><dd>{generation.performanceInstruction ?? "未提供（历史任务只使用自动演绎）"}</dd></div> : null}
              </dl></details>
            </article>;
          })}
        </div>
      </section>
    </div>
  );
}
